/**
 * Payload construction hook for Generate Assessment Plan
 *
 * Orchestrates dxList normalization, payload assembly, gateway API call,
 * and parent notification. Only sends postMessage to parent after
 * receiving a response from the gateway endpoint.
 */

import {
  buildGenerateAssessmentPlanPayload,
  normalizeDxList,
} from '@/lib/generate-assessment-plan-utils';
import { getGatewayConfig } from '@/lib/env-config';
import type {
  GenerateAssessmentPlanParams,
  GenerateAssessmentPlanPayload,
} from '@/types/generate-assessment-plan';
import { useEffect, useRef, useState } from 'react';

interface UseGenerateAssessmentPlanPayloadParams {
  params: GenerateAssessmentPlanParams;
  scribeJobId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawDxList: any;
  partnerId: string;
  gatewayToken: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendToParent: (type: string, payload: any) => void;
}

export const useGenerateAssessmentPlanPayload = ({
  params,
  scribeJobId,
  rawDxList,
  partnerId,
  gatewayToken,
  sendToParent,
}: UseGenerateAssessmentPlanPayloadParams) => {
  const [payload, setPayload] = useState<GenerateAssessmentPlanPayload | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const hasBuilt = useRef(false);

  useEffect(() => {
    // Wait for all required inputs
    if (!scribeJobId || !rawDxList || !gatewayToken || hasBuilt.current) {
      return;
    }

    hasBuilt.current = true;
    setIsGenerating(true);
    setError(null);

    const generate = async () => {
      try {
        console.log('[GenerateAssessmentPlan] Building payload with:', {
          scribeJobId,
          partnerId: partnerId || '(none)',
          dxListLength: Array.isArray(rawDxList) ? rawDxList.length : 'N/A',
          hasGatewayToken: !!gatewayToken,
        });

        // Normalize dxList
        const normalizedDxList = normalizeDxList(rawDxList);
        console.log(
          '[GenerateAssessmentPlan] Normalized dxList:',
          JSON.stringify(normalizedDxList, null, 2),
        );

        // Build the final payload
        const constructedPayload = buildGenerateAssessmentPlanPayload(
          params,
          scribeJobId,
          normalizedDxList,
          partnerId,
        );

        console.log(
          '[GenerateAssessmentPlan] ✅ Constructed payload:',
          JSON.stringify(constructedPayload, null, 2),
        );

        setPayload(constructedPayload);

        // Call gateway endpoint
        const gatewayConfig = getGatewayConfig();
        const endpointUrl = `${gatewayConfig.baseUrl}/api/scribe/generate-assessment-plan`;

        console.log(
          '[GenerateAssessmentPlan] 📤 Calling gateway:',
          endpointUrl,
        );

        const response = await fetch(endpointUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${gatewayToken}`,
          },
          body: JSON.stringify(constructedPayload),
        });

        console.log('[GenerateAssessmentPlan] Gateway response status:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            '[GenerateAssessmentPlan] ❌ Gateway error response:',
            errorText,
          );
          setError(
            `Gateway error: ${response.status} - ${errorText || response.statusText}`,
          );
          setIsGenerating(false);
          return;
        }

        const responseData = await response.json();
        console.log(
          '[GenerateAssessmentPlan] ✅ Gateway response data:',
          JSON.stringify(responseData, null, 2),
        );

        // Send response to parent window only after successful gateway call
        sendToParent('generateAssessmentPlanResponse', {
          success: true,
          request: constructedPayload,
          response: responseData,
        });

        console.log(
          '[GenerateAssessmentPlan] ✅ Sent response to parent window',
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to generate assessment plan';
        console.error(
          '[GenerateAssessmentPlan] ❌ Generation failed:',
          errorMessage,
          err,
        );
        setError(errorMessage);
        setIsGenerating(false);
      }
    };

    generate();
  }, [scribeJobId, rawDxList, partnerId, gatewayToken, params, sendToParent]);

  return {
    payload,
    error,
    isGenerating,
    isReady: payload !== null,
  };
};
